// "Add valuation" slideover. Opened by the index "Add valuation" button via the
// window event "open-add-valuation-slideover". Mints the new-business graph
// (Landlord + Property + PropertyOwnership + Valuation) through POST /valuations,
// then opens the new record's slideover.
const blankForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  house_number: "",
  street: "",
  town: "",
  postcode: "",
  property_type: "",
  beds: null,
  status: "requested",
  valuation_at: "",
  notes: "",
  // Doorway grounding — an existing landlord/property this lead attaches to.
  landlord_id: null,
  property_id: null,
  // The inbox's doorways: the thread the lead arrived on (linked to the new
  // row on save) and, for the agency's own website form, its source.
  email_thread_id: null,
  source: null,
});

export function addValuationSlideover() {
  return {
    open: false,
    saving: false,
    formErrors: {},
    zIndex: 100010,
    form: blankForm(),
    // Set when a create_valuation doorway card opened this form: posted back
    // so the card resolves as executed by the real path.
    proposedActionId: null,
    // A landlord already on file this lead belongs to. Picked from the
    // matches offered as the name/email is typed (or carried in by a card's
    // grounding); while set, the contact fields give way to it and the
    // server attaches to that landlord instead of minting a twin.
    selectedLandlord: null,
    landlordMatches: [],
    landlordSearchSeq: 0,

    init() {
      window.addEventListener("open-add-valuation-slideover", (e) => this.openForCreate(e?.detail));
    },

    openForCreate(detail = {}) {
      const store = Alpine.store?.("slideovers");
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex();
      this.form = blankForm();
      // A create_valuation card arrives with the lead already read out of the
      // correspondence; the human checks it and presses Add.
      const prefill = detail?.prefill || {};
      Object.keys(prefill).forEach((key) => {
        if (key in this.form && prefill[key] !== null && prefill[key] !== undefined) {
          this.form[key] = prefill[key];
        }
      });
      this.proposedActionId = detail?.proposed_action_id || null;
      this.landlordMatches = [];
      this.selectedLandlord = this.form.landlord_id ? {
        id: this.form.landlord_id,
        name: [this.form.first_name, this.form.last_name].filter((p) => p && p.trim()).join(" ") || "Landlord on file",
        email: this.form.email,
        phone: this.form.phone,
      } : null;
      this.formErrors = {};
      this.saving = false;
      this.open = true;
    },

    close() {
      this.open = false;
    },

    // What the user has typed so far, as a search: the email when there is
    // one (it pins a person exactly), otherwise the name.
    async findLandlordMatches() {
      if (this.selectedLandlord) return;
      const email = this.form.email.trim();
      const name = [this.form.first_name, this.form.last_name].map((p) => p.trim()).filter(Boolean).join(" ");
      const query = email.includes("@") ? email : name;
      const seq = ++this.landlordSearchSeq;
      if (query.length < 3) {
        this.landlordMatches = [];
        return;
      }
      try {
        const response = await fetch(`/landlords/search.json?q=${encodeURIComponent(query)}`, {
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        });
        const landlords = response.ok ? await response.json() : [];
        if (seq === this.landlordSearchSeq) this.landlordMatches = landlords.slice(0, 5);
      } catch (_) {
        if (seq === this.landlordSearchSeq) this.landlordMatches = [];
      }
    },

    selectLandlord(landlord) {
      this.selectedLandlord = landlord;
      this.form.landlord_id = landlord.id;
      this.landlordMatches = [];
      delete this.formErrors.first_name;
      delete this.formErrors.last_name;
    },

    clearLandlord() {
      this.selectedLandlord = null;
      this.form.landlord_id = null;
      this.form.first_name = "";
      this.form.last_name = "";
      this.form.email = "";
      this.form.phone = "";
    },

    get headerTitle() {
      if (this.selectedLandlord) return this.selectedLandlord.name;
      const name = [this.form.first_name, this.form.last_name].filter((p) => p && p.trim()).join(" ");
      return name || "New valuation";
    },

    validate() {
      this.formErrors = {};
      if (!this.form.landlord_id) {
        if (!this.form.first_name.trim()) this.formErrors.first_name = "Required";
        if (!this.form.last_name.trim()) this.formErrors.last_name = "Required";
      }
      if (!this.form.postcode.trim()) this.formErrors.postcode = "Required";
      if (Object.keys(this.formErrors).length) {
        this.formErrors.base = "Enter the landlord's name and the property postcode.";
        return false;
      }
      return true;
    },

    async createValuation() {
      if (this.saving || !this.validate()) return;
      this.saving = true;

      try {
        const response = await fetch("/valuations.json", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify({ valuation: this.form, proposed_action_id: this.proposedActionId }),
        });
        let data = null;
        try { data = await response.json(); } catch (_) { data = null; }

        if (response.ok && data && data.success) {
          window.dispatchEvent(new CustomEvent("valuation-created", {
            detail: { valuation: data.valuation, email_thread_id: this.form.email_thread_id },
          }));
          this.close();
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("open-valuation-slideover", { detail: { id: data.valuation.id } }));
          }, 300);
        } else if (data && data.errors && typeof data.errors === "object") {
          Object.entries(data.errors).forEach(([field, messages]) => {
            this.formErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          });
          this.formErrors.base = data.errors.base ? (Array.isArray(data.errors.base) ? data.errors.base[0] : data.errors.base) : "Please fix the highlighted fields and try again.";
        } else {
          this.formErrors.base = `Couldn't save the valuation (server error ${response.status}). Please try again.`;
        }
      } catch (e) {
        this.formErrors.base = "Couldn't reach the server. Check your connection and try again.";
      } finally {
        this.saving = false;
      }
    },
  };
}
